import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  THead,
  TrHead,
  TBody,
  Th,
  Tr,
  Td,
  InnerCell,
} from "@teamrota/rota-design";

import { StreamProvider } from "@teamrota/shiftstreamlib";

import IconLink from "~/src/containers/payroll/components/IconLink";
import Icon from "~/src/components/icon";
import { EmptyText } from "~/src/components/payroll/style";

import { SCRAPE } from "./graphql/mutation/scrape";
import { ScrollableTable } from "../styles";
import { COL_WIDTHS } from ".";
import { cleanAddress, hasExternalSystem, toHumanDuration } from "../../utils";
import { shiftStreamClient } from "../../graphql/client";
import { useMutation } from "@apollo/client";
import { ResultModal } from "../../components/result";

interface TableProps {
  accounts: any[];
  loading: boolean;
}

const Table: React.FC<TableProps> = ({ accounts, loading }) => {
  const history = useHistory();

  const [resultModal, setResultModal] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);

  const [scrape, { loading: isScrapeLoading }] = useMutation(SCRAPE, {
    client: shiftStreamClient,
  });

  const handleClickStreams = (account: any) => {
    history.push(`/shiftstream/streams/account/${account.id}`);
  };

  const handleClickEmails = (account: any) => {
    history.push(`/shiftstream/emails/account/${account.id}`);
  };

  const handleClickScheduleScrape = async (account: any) => {
    try {
      const result = await scrape({ variables: { id: parseInt(account.id) } });
      if (result?.data?.scrape) {
        setResultModal(result.data.scrape);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  return (
    <>
      <ScrollableTable colWidths={COL_WIDTHS}>
        <THead>
          <TrHead>
            <Th>Account ID</Th>
            <Th>Account Name</Th>
            <Th>Provider</Th>
            <Th>Roster Sync</Th>
            <Th>Unfilled Check</Th>
            <Th>Algo Run</Th>
            <Th>Fulfil Submitted</Th>
            <Th>&nbsp; </Th>
          </TrHead>
        </THead>

        <TBody className="streams-table-scroll-body">
          {accounts?.length ? (
            accounts.map((account: any) => {
              const isEmailProvider = account.provider === StreamProvider.EMAIL;
              const hasExternalLink = hasExternalSystem(account.provider);

              return (
                <Tr key={account.id}>
                  <Td>
                    <InnerCell>#{account.id}</InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      {hasExternalLink ? (
                        <a
                          href={account.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {account.name}
                        </a>
                      ) : (
                        <>{account.name}</>
                      )}

                      <div style={{ fontSize: "70%" }}>
                        {isEmailProvider ? (
                          <>
                            Email:{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {cleanAddress(account.url)}
                            </span>
                          </>
                        ) : hasExternalLink ? (
                          <>
                            Login:{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {account.login}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </InnerCell>
                  </Td>

                  <Td>
                    <img
                      title={account.name}
                      src={`/images/shiftstream/${account.provider}.png`}
                      alt={`${account.provider} logo`}
                      style={{ height: 20, verticalAlign: "middle" }}
                    />
                  </Td>

                  <Td>
                    <InnerCell>
                      {toHumanDuration(account.metrics.lastRosterSync)}
                    </InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      {toHumanDuration(account.metrics.lastUnfilledCheck)}
                    </InnerCell>
                  </Td>

                  <Td>
                    <InnerCell>
                      {toHumanDuration(account.metrics.lastAlgo)}
                    </InnerCell>
                  </Td>
                  <Td>
                    <InnerCell>
                      {toHumanDuration(account.metrics.lastFulfil)}
                    </InnerCell>
                  </Td>
                  <Td>
                    <InnerCell>
                      <IconLink
                        name="ABACUS"
                        size={20}
                        isButton
                        color={Icon.colors.MAIN_ORANGE}
                        tooltip="View streams"
                        onClick={() => handleClickStreams(account)}
                      />

                      {isEmailProvider && (
                        <IconLink
                          name="EMAIL"
                          size={20}
                          isButton
                          color={Icon.colors.MAIN_ORANGE}
                          tooltip="View Emails"
                          onClick={() => handleClickEmails(account)}
                        />
                      )}

                      {hasExternalLink && (
                        <IconLink
                          name="REFRESH"
                          size={20}
                          isButton
                          color={Icon.colors.MAIN_ORANGE}
                          tooltip="Schedule a new roster scrape"
                          isDisabled={isScrapeLoading}
                          onClick={() => handleClickScheduleScrape(account)}
                        />
                      )}
                    </InnerCell>
                  </Td>
                </Tr>
              );
            })
          ) : (
            <Tr>
              <Td colSpan={8}>
                <EmptyText>
                  {!loading && "None found 👀"}
                  {loading && "Loading... ⏳"}
                </EmptyText>
              </Td>
            </Tr>
          )}
        </TBody>
      </ScrollableTable>

      {resultModal && (
        <ResultModal
          message={resultModal.message ?? null}
          isError={resultModal.isError ?? false}
          onDismiss={() => setResultModal(null)}
        />
      )}
    </>
  );
};

export default Table;
